import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router';
import { toast } from 'react-toastify';
import { z } from 'zod';
import { useEdiInvitationAccept, useEdiInvitationValidate } from '@/api/hanly-v2/edi/edi';
import { Button } from '@/components/ui/button';
import { FormError } from '@/components/ui/form-error';
import { Label } from '@/components/ui/label';
import { PasswordInput } from '@/components/ui/password-input';
import { passwordConfirmSchema, passwordSchema, usernameSchema } from '@/utils/schemas';

const acceptInvitationSchema = z
  .object({
    username: usernameSchema,
    password: passwordSchema,
    confirmPassword: passwordConfirmSchema,
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'パスワードが一致しません',
    path: ['confirmPassword'],
  });

type FormValues = z.infer<typeof acceptInvitationSchema>;

export const EdiInvitationAcceptPage = () => {
  const { invitationKey } = useParams<{ invitationKey: string }>();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const ediInvitationAcceptMutation = useEdiInvitationAccept();

  const {
    mutate: validateInvitation,
    data: validationData,
    error: validationError,
    isIdle,
    isPending,
  } = useEdiInvitationValidate();
  const validatedKeyRef = useRef<string | null>(null);
  const isLoading = !!invitationKey && (isIdle || isPending);

  // 検証は既存ユーザーを有効化する副作用があるため POST。招待キーごとに1回だけ呼ぶ
  useEffect(() => {
    if (!invitationKey || validatedKeyRef.current === invitationKey) return;
    validatedKeyRef.current = invitationKey;
    validateInvitation({ data: { invitation_key: invitationKey } });
  }, [invitationKey, validateInvitation]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(acceptInvitationSchema),
  });

  // 既存ユーザーでアクティベーション済みの場合
  useEffect(() => {
    if (validationData?.result === 'activated') {
      toast.success('アカウントが有効化されました。ログインしてください。');
      navigate('/signin');
    }
  }, [validationData, navigate]);

  // バリデーションエラー
  useEffect(() => {
    if (validationError) {
      setError('招待リンクが無効または期限切れです。');
    }
  }, [validationError]);

  const onSubmit = async (data: FormValues) => {
    if (!invitationKey) {
      setError('招待キーが見つかりません');
      return;
    }

    setError('');
    setIsSubmitting(true);

    try {
      await ediInvitationAcceptMutation.mutateAsync({
        data: {
          invitation_key: invitationKey,
          username: data.username,
          password: data.password,
        },
      });

      toast.success('アカウントが作成されました。ログインしてください。');
      navigate('/signin');
    } catch (_err) {
      setError('アカウントの作成中にエラーが発生しました。招待リンクが無効か期限切れの可能性があります。');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex-1 flex justify-center items-center">
        <p className="text-neutral-600">読み込み中...</p>
      </div>
    );
  }

  // エラー時やactivated後はフォームを表示しない
  if (validationError || validationData?.result === 'activated') {
    return (
      <div className="flex-1 flex justify-center items-center">
        <div className="max-w-md mx-auto text-center">{error && <p className="text-red-600">{error}</p>}</div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex justify-center items-center">
      <div className="max-w-md mx-auto">
        <div className="mb-5">
          <h2>アカウントを作成する</h2>
          <p>{validationData?.organization_name}から招待が届いています。</p>
          <p className="mt-1">下記情報を入力してアカウントを作成してください</p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="space-y-2 mb-5">
            <div>
              <Label htmlFor="username" required>
                お名前
              </Label>
              <input
                id="username"
                type="text"
                placeholder="例）山田 太郎"
                autoComplete="name"
                {...register('username')}
              />
              <FormError message={errors.username?.message} />
            </div>

            <div>
              <Label htmlFor="email">メールアドレス</Label>
              <input
                id="email"
                type="email"
                value={validationData?.email ?? ''}
                readOnly
                autoComplete="username"
                className="bg-neutral-100"
              />
            </div>

            <div>
              <Label htmlFor="password" required>
                パスワード
              </Label>
              <PasswordInput
                id="password"
                className="h-11"
                autoComplete="new-password"
                {...register('password')}
              />
              <FormError message={errors.password?.message} />
            </div>

            <div>
              <Label htmlFor="confirmPassword" required>
                パスワード（確認）
              </Label>
              <PasswordInput
                id="confirmPassword"
                className="h-11"
                autoComplete="new-password"
                {...register('confirmPassword')}
              />
              <FormError message={errors.confirmPassword?.message} />
            </div>
          </div>

          <FormError message={error} />

          <div>
            <Button type="submit" disabled={isSubmitting || !!validationError} className="w-full">
              {isSubmitting ? 'アカウント作成中...' : 'アカウントを作成'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
